import React, { FC, useMemo } from 'react';
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { formatPrice } from '../../../selectors';
import { AbilityModel } from '../../../models/AbilityModel';
import { HeaderModel } from '../../../models/CommonModel';
import SortableColumn from '../../../components/Table/SortableColumn';
import moment from 'moment';

type Props = {
    data: Array<AbilityModel>,
    handleExportPdf: (id: number) => void,
    header: HeaderModel[],
    hideCols: string[],
    openColConfig: () => void,
    handleSort: (sortBy: string, direction: 'asc' | 'desc' | 'none') => void
    hideConfig?: boolean
    handleEditItem: (item: AbilityModel) => void,
    handleCheck: (e: any, item: AbilityModel) => void
    handleCheckAll: (e: any) => void,
    selectedIds: number[]
    canEdit: boolean
};

const ReceiptVoucherTable: FC<Props> = ({ 
    data, handleExportPdf, 
    header, 
    hideCols, 
    openColConfig, 
    handleSort, 
    handleEditItem, 
    selectedIds,
    hideConfig,
    handleCheckAll,
    handleCheck,
    canEdit
}) => {
    const intl = useIntl();
    const history = useHistory();
    const paymentMethod = (method: string) => {
        if (method == 'banking') {
            return 'Chuyển khoản';
        } else if (method == 'cash') {
            return 'Tiền mặt';
        }
        return '';
    }

    const renderPurpose = (purpose: string | null) => {
        switch (purpose) {
            case 'withdraw':
                return 'Rút tiền mặt';
            case 'deposit':
                return 'Nộp tiền ngân hàng';
            case 'bank_disburses_funds':
                return 'Ngân hàng giải ngân';
            case 'interest_on_deposit':
                return 'Lãi tiền gửi';
            default:
                return 'Công nợ';
        }
    }

    const headerFiels = header?.map((e) => e.field) ?? [];
    const isChecked = (id: number) => selectedIds.includes(id);
    const isCheckedAll = data.length > 0 && data.every(item => selectedIds.includes(item.id));
    
    return (
        <div className='table-responsive'>
            <table className="table table-hover table-bordered table-striped border gy-7 gs-7">
                <thead>
                    <tr className="fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200">
                    {!hideConfig && (
                        <th>
                            <i
                                className="fa fa-cogs"
                                style={{ cursor: 'pointer' }}
                                onClick={openColConfig}
                            ></i>
                        </th>
                    )}
                    <th>
                        <input  className="form-check-input" type="checkbox"
                            checked={isCheckedAll}
                            onChange={handleCheckAll}
                        />
                    </th>
                    {header.map((th, index) => {
                        if (!hideCols.includes(th.field)) {
                            if (th.sortable) {
                                return (
                                    <SortableColumn
                                        key={'th-' + index}
                                        sortBy={th?.field}
                                        onSortChange={handleSort}
                                        style={th?.style ?? {}}
                                    >
                                        {th.label}
                                    </SortableColumn>
                                )
                            }
                            return (
                                <th style={th?.style ?? {}}>{th.label}</th>
                            )
                        }
                    })}
                    <th></th>
                    </tr>
                </thead>
                <tbody>
                    {data?.map((item: AbilityModel, index: number) => {
                        const checked = isChecked(item.id);
                        const invoices = item.order?.invoices ?? (item.receipt?.invoices ?? [])
                        const lastInvoice = invoices?.length ? invoices[invoices.length - 1] : null;
                        let invoiceStr = null;
                        if (lastInvoice) {
                            if (item.ref_order_id) {
                                invoiceStr = lastInvoice.invoice_template_code+lastInvoice.invoice_no
                            } else {
                                invoiceStr = lastInvoice.label
                            }
                        }

                        let note = item.note ?? '';
                        if (invoiceStr && !note.includes(invoiceStr)) {
                            note += ' | HĐ: ' + invoiceStr
                        }

                        let prefix = 'PT';
                        if (item.payment_method === 'banking') {
                            prefix = 'TNH';
                        }
                        return (
                            <tr key={`ability_${index}`} className="cursor-pointer">
                                {!hideConfig && (
                                <td></td>
                                )}
                                <td>
                                    <div className="form-check form-check-custom">
                                        <input key={`item_checkbox_${item.id}_${checked}`} className="form-check-input" type="checkbox"
                                            value={item.id}
                                            defaultChecked={checked}
                                            onChange={(e) => handleCheck(e, item)}/>
                                    </div>
                                </td>
                                {(headerFiels.includes('id') && !hideCols.includes('id')) && (
                                <td>{prefix}{item.id}</td>
                                )}
                                {!hideCols.includes('created_at') && (
                                <td>{moment(item.created_at).format('DD-MM-YYYY')}</td>
                                )}
                                {!hideCols.includes('date') && (
                                <td>{item.date ?? ''}</td>
                                )}
                                {!hideCols.includes('name') && (
                                <td>{item.name}</td>
                                )}
                                {!hideCols.includes('bank_id') && (
                                    <td className='text-center'>{item?.bank ? item.bank.bank_name + ' - ' + item.bank.bank_account_number : ''}</td>
                                )}
                                {!hideCols.includes('note') && (
                                <td>{note}</td>
                                )}
                                {!hideCols.includes('value') && (
                                <td>{formatPrice(item.value)}</td>
                                )}
                                {!hideCols.includes('purpose') && (
                                <td>{ renderPurpose(item.purpose ?? null) }</td>
                                )}
                                {!hideCols.includes('payment_method') && (
                                <td>
                                    { paymentMethod(item.payment_method ?? '') }
                                </td>
                                )}
                                <td>
                                    {canEdit && (
                                        <button 
                                            className="btn btn-primary me-3 btn-sm btn-icon" 
                                            type="button"
                                            onClick={() => handleEditItem(item)}
                                        >
                                            <i className='bi bi-pencil-square'></i>
                                        </button>
                                    )}
                                    <button 
                                        className="btn btn-primary me-3  btn-sm" 
                                        type="button"
                                        onClick={() => handleExportPdf(item.id)}
                                    >
                                        {intl.formatMessage({id: 'ORDER.PRINT'})}
                                    </button>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default ReceiptVoucherTable;